import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Header from '../../components/ui/Header';
import VerificationSection from './components/VerificationSection';
import ProfileHeader from './components/ProfileHeader';
import PrivacySettings from './components/PrivacySettings';
import Produit from './components/Produit';
import Icon from '../../components/AppIcon';

const UserProfile = () => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [statsData, setStatsData] = useState({
    activeListings: 0,
    totalSales: 0,
    responseRate: 0
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [isProUser, setIsProUser] = useState(false);
  
  // États pour le formulaire d'édition
  const [editForm, setEditForm] = useState({
    nom: '',
    email: '',
    telephone: '',
    adresse: '',
    avatar: '',
    newAvatar: null,
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    nom_pro: '',
    type_pro: '',
    descriptions: '',
    specialites: '',
    businessEmail: '',
    businessWebsite: '',
    businessHours: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [fieldTouched, setFieldTouched] = useState({});

  const navigate = useNavigate();
  const location = useLocation();
  const API_URL = 'https://api.autocasses.com';

  const tabs = [
    { id: 'overview', label: t('userProfile.tabs.overview'), icon: 'User' },
    { id: 'produits', label: t('userProfile.tabs.products'), icon: 'Receipt' },
    { id: 'verification', label: t('userProfile.tabs.verification'), icon: 'ShieldCheck' },
    { id: 'privacy', label: t('userProfile.tabs.privacy'), icon: 'Settings' }
  ];

  // VALIDATION AVANCÉE DU FORMAT EMAIL
  const validateEmailFormat = (email) => {
    if (!email || email.trim() === '') {
      return {
        isValid: false,
        message: t('userProfile.errors.email.empty')
      };
    }

    const trimmedEmail = email.trim();
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    
    if (!emailRegex.test(trimmedEmail)) {
      return {
        isValid: false,
        message: t('userProfile.errors.email.invalidFormat')
      };
    }

    const [localPart, domainPart] = trimmedEmail.split('@');
    
    if (domainPart.length > 253) {
      return {
        isValid: false,
        message: t('userProfile.errors.email.domainTooLong')
      };
    }

    if (localPart.length > 64) {
      return {
        isValid: false,
        message: t('userProfile.errors.email.localPartTooLong')
      };
    }

    const invalidLocalPartRegex = /[<>()\[\]\\.,;:\s@"]/;
    if (invalidLocalPartRegex.test(localPart)) {
      return {
        isValid: false,
        message: t('userProfile.errors.email.invalidCharacters')
      };
    }

    if (localPart.startsWith('.') || localPart.endsWith('.') || localPart.includes('..')) {
      return {
        isValid: false,
        message: t('userProfile.errors.email.invalidDots')
      };
    }

    const domainRegex = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!domainRegex.test(domainPart)) {
      return {
        isValid: false,
        message: t('userProfile.errors.email.invalidDomain')
      };
    }

    return {
      isValid: true,
      message: '',
      sanitizedEmail: trimmedEmail
    };
  };

  // Validation spécifique pour email business
  const validateBusinessEmailFormat = (email) => {
    if (!email || email.trim() === '') {
      return {
        isValid: true,
        message: '',
        sanitizedEmail: ''
      };
    }

    const validation = validateEmailFormat(email);
    if (!validation.isValid) {
      return {
        isValid: false,
        message: t('userProfile.errors.businessEmail.invalidFormat')
      };
    }

    return validation;
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  useEffect(() => {
    if (userData && showEditModal) {
      setEditForm({
        nom: userData.nom || '',
        email: userData.email || '',
        telephone: userData.telephone || '',
        adresse: userData.adresse || '',
        avatar: userData.avatar || '',
        newAvatar: null,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        nom_pro: userData.nom_pro || '',
        type_pro: userData.type_pro || t('userProfile.userTypes.garage'),
        descriptions: userData.descriptions || '',
        specialites: userData.specialites || '',
        businessEmail: userData.businessEmail || '',
        businessWebsite: userData.businessWebsite || '',
        businessHours: userData.businessHours || ''
      });
      setIsProUser(userData.isPro || false);
      setFieldTouched({});
      setFormErrors({});
    }
  }, [userData, showEditModal]);

  // Fonction pour récupérer les données utilisateur
  const fetchUserData = async () => {
    try {
      setIsLoading(true);
      const storedUserData = localStorage.getItem('userData');
      const token = localStorage.getItem('userToken');
      
      if (!storedUserData) {
        alert(t('userProfile.alerts.sessionExpired'));
        navigate('/auth');
        return;
      }

      const User = JSON.parse(storedUserData);
      
      if (!User || !User.id) {
        alert(t('userProfile.alerts.sessionExpired'));
        navigate('/auth');
        return;
      }

      const response = await axios.get(`${API_URL}/api/user/get/${User.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 200) {
        const apiData = response.data;
        
        const formattedData = {
          id: apiData.id,
          nom: apiData.nom || t('userProfile.emptyFields.user'),
          email: apiData.email || '',
          telephone: apiData.telephone || '',
          adresse: apiData.adresse || t('userProfile.emptyFields.location'),
          avatar: apiData.avatar || null,
          isVerified: apiData.isVerified || false,
          isPro: apiData.isPro || false,
          role: apiData.role || 'utilisateur',
          createdAt: apiData.createdAt,
          type_pro: apiData.type_pro || t('userProfile.userTypes.garage'),
          nom_pro: apiData.nom_pro || '',
          tel_pro: apiData.tel_pro || '',
          descriptions: apiData.descriptions || '',
          specialites: apiData.specialites || '',
          businessAddress: apiData.adresse || '',
          businessEmail: apiData.businessEmail || '',
          businessWebsite: apiData.businessWebsite || '',
          businessHours: apiData.businessHours || ''
        };

        setUserData(formattedData);
        setIsProUser(apiData.isPro || false);
        await fetchUserStats(User.id);
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Erreur lors de la récupération des données:', error);
      
      try {
        const storedUserData = localStorage.getItem('userData');
        if (storedUserData) {
          const fallbackData = JSON.parse(storedUserData);
          setUserData({
            id: fallbackData.id,
            nom: fallbackData.nom || fallbackData.name || t('userProfile.emptyFields.user'),
            email: fallbackData.email || t('userProfile.profileHeader.noEmail'),
            telephone: fallbackData.telephone || '',
            adresse: fallbackData.adresse || t('userProfile.emptyFields.location'),
            avatar: null,
            isVerified: fallbackData.isVerified || false,
            isPro: fallbackData.isPro || false,
            role: fallbackData.role || 'utilisateur'
          });
          setIsProUser(fallbackData.isPro || false);
        }
      } catch (fallbackError) {
        console.error('Erreur fallback:', fallbackError);
      }
      
      setIsLoading(false);
    }
  };

  // Fonction de rafraîchissement
  const handleProfileUpdate = useCallback(async () => {
    setRefreshing(true);
    try {
      await fetchUserData();
      setRefreshing(false);
    } catch (error) {
      console.error('Erreur lors du rafraîchissement:', error);
      setRefreshing(false);
    }
  }, []);

  const fetchUserStats = async (userId) => {
    try {
      // Ici, vous pouvez appeler votre API pour les statistiques
      // Pour l'instant, on simule des données
      const stats = {
        activeListings: 12,
        totalSales: 45,
        responseRate: 95
      };
      setStatsData(stats);
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
    }
  };

  const handlePickImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        if (file.size > 5 * 1024 * 1024) { // 5MB limit
          alert(t('userProfile.alerts.imageTooLarge'));
          return;
        }
        const reader = new FileReader();
        reader.onload = (event) => {
          setEditForm(prev => ({
            ...prev,
            avatar: event.target.result,
            newAvatar: file
          }));
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  };

  const handleRemoveAvatar = () => {
    if (window.confirm(t('userProfile.alerts.removePhotoConfirm'))) {
      setEditForm(prev => ({
        ...prev,
        avatar: null,
        newAvatar: null
      }));
    }
  };

  // Validation en temps réel
  const validateField = (fieldName, value) => {
    const errors = { ...formErrors };
    
    switch (fieldName) {
      case 'nom':
        if (!value.trim()) {
          errors.nom = t('userProfile.errors.name.required');
        } else if (value.trim().length < 2) {
          errors.nom = t('userProfile.errors.name.tooShort');
        } else {
          delete errors.nom;
        }
        break;
        
      case 'email':
        const emailValidation = validateEmailFormat(value);
        if (!emailValidation.isValid) {
          errors.email = emailValidation.message;
        } else {
          delete errors.email;
        }
        break;
        
      case 'telephone':
        if (isProUser && !value.trim()) {
          errors.telephone = t('userProfile.errors.phone.proRequired');
        } else if (value.trim() && !/^[0-9]{10,15}$/.test(value.trim())) {
          errors.telephone = t('userProfile.errors.phone.invalid');
        } else {
          delete errors.telephone;
        }
        break;
        
      case 'nom_pro':
        if (isProUser && !value.trim()) {
          errors.nom_pro = t('userProfile.errors.companyName.required');
        } else if (isProUser && value.trim().length < 2) {
          errors.nom_pro = t('userProfile.errors.companyName.tooShort');
        } else {
          delete errors.nom_pro;
        }
        break;
        
      case 'businessEmail':
        if (value.trim() && value.trim() !== '') {
          const businessEmailValidation = validateBusinessEmailFormat(value);
          if (!businessEmailValidation.isValid) {
            errors.businessEmail = businessEmailValidation.message;
          } else {
            delete errors.businessEmail;
          }
        } else {
          delete errors.businessEmail;
        }
        break;
        
      case 'newPassword':
        if (value && value.length < 6) {
          errors.newPassword = t('userProfile.errors.password.tooShort');
        } else if (value && editForm.confirmPassword && value !== editForm.confirmPassword) {
          errors.confirmPassword = t('userProfile.errors.password.mismatch');
          errors.newPassword = t('userProfile.errors.password.mismatch');
        } else {
          delete errors.newPassword;
          if (editForm.confirmPassword && value === editForm.confirmPassword) {
            delete errors.confirmPassword;
          }
        }
        break;
        
      case 'confirmPassword':
        if (editForm.newPassword && value !== editForm.newPassword) {
          errors.confirmPassword = t('userProfile.errors.password.mismatch');
        } else {
          delete errors.confirmPassword;
        }
        break;
        
      case 'currentPassword':
        if (editForm.newPassword && !value.trim()) {
          errors.currentPassword = t('userProfile.errors.password.currentRequired');
        } else {
          delete errors.currentPassword;
        }
        break;
    }
    
    setFormErrors(errors);
  };

  // Validation complète du formulaire AVANT ENVOI
  const validateFormBeforeSubmit = () => {
    const errors = {};
    let hasCriticalErrors = false;
    
    // Validation du nom
    if (!editForm.nom.trim()) {
      errors.nom = t('userProfile.errors.name.required');
      hasCriticalErrors = true;
    } else if (editForm.nom.trim().length < 2) {
      errors.nom = t('userProfile.errors.name.tooShort');
      hasCriticalErrors = true;
    }
    
    // Validation STRICTE de l'email avant envoi
    const emailValidation = validateEmailFormat(editForm.email);
    if (!emailValidation.isValid) {
      errors.email = emailValidation.message;
      hasCriticalErrors = true;
    }
    
    // Validation spécifique pour les comptes pro
    if (isProUser) {
      // Nom de l'entreprise requis
      if (!editForm.nom_pro.trim()) {
        errors.nom_pro = t('userProfile.errors.companyName.required');
        hasCriticalErrors = true;
      } else if (editForm.nom_pro.trim().length < 2) {
        errors.nom_pro = t('userProfile.errors.companyName.tooShort');
        hasCriticalErrors = true;
      }
      
      // Téléphone requis pour les pros
      if (!editForm.telephone.trim()) {
        errors.telephone = t('userProfile.errors.phone.proRequired');
        hasCriticalErrors = true;
      } else if (!/^[0-9]{10,15}$/.test(editForm.telephone.trim())) {
        errors.telephone = t('userProfile.errors.phone.invalid');
        hasCriticalErrors = true;
      }
      
      // Validation STRICTE de l'email professionnel si fourni
      if (editForm.businessEmail && editForm.businessEmail.trim() !== '') {
        const businessEmailValidation = validateBusinessEmailFormat(editForm.businessEmail);
        if (!businessEmailValidation.isValid) {
          errors.businessEmail = businessEmailValidation.message;
          hasCriticalErrors = true;
        }
      }
    } else {
      // Validation du téléphone pour les utilisateurs standard
      if (editForm.telephone && editForm.telephone.trim() !== '' && !/^[0-9]{10,15}$/.test(editForm.telephone.trim())) {
        errors.telephone = t('userProfile.errors.phone.invalid');
        hasCriticalErrors = true;
      }
    }
    
    // Validation du mot de passe
    if (editForm.newPassword) {
      if (!editForm.currentPassword) {
        errors.currentPassword = t('userProfile.errors.password.currentRequired');
        hasCriticalErrors = true;
      }
      
      if (editForm.newPassword.length < 6) {
        errors.newPassword = t('userProfile.errors.password.tooShort');
        hasCriticalErrors = true;
      }
      
      if (editForm.newPassword !== editForm.confirmPassword) {
        errors.confirmPassword = t('userProfile.errors.password.mismatch');
        errors.newPassword = t('userProfile.errors.password.mismatch');
        hasCriticalErrors = true;
      }
    }
    
    setFormErrors(errors);
    return !hasCriticalErrors;
  };

  const handleFieldChange = (fieldName, value) => {
    setEditForm(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // Marquer le champ comme touché
    setFieldTouched(prev => ({
      ...prev,
      [fieldName]: true
    }));
    
    // Validation en temps réel
    validateField(fieldName, value);
  };

  // VALIDATION ET NORMALISATION DES EMAILS AVANT ENVOI
  const sanitizeAndValidateEmails = () => {
    // Validation et nettoyage de l'email principal
    const emailValidation = validateEmailFormat(editForm.email);
    if (!emailValidation.isValid) {
      alert(emailValidation.message);
      return null;
    }

    // Validation et nettoyage de l'email professionnel si fourni
    let sanitizedBusinessEmail = '';
    if (editForm.businessEmail && editForm.businessEmail.trim() !== '') {
      const businessEmailValidation = validateBusinessEmailFormat(editForm.businessEmail);
      if (!businessEmailValidation.isValid) {
        alert(businessEmailValidation.message);
        return null;
      }
      sanitizedBusinessEmail = businessEmailValidation.sanitizedEmail;
    }

    return {
      email: emailValidation.sanitizedEmail,
      businessEmail: sanitizedBusinessEmail
    };
  };

  const handleUpdateProfile = async () => {
    // Marquer tous les champs comme touchés pour afficher toutes les erreurs
    const allFields = {
      nom: true,
      email: true,
      telephone: true,
      ...(isProUser ? {
        nom_pro: true,
        businessEmail: !!editForm.businessEmail,
      } : {}),
      ...(editForm.newPassword ? {
        currentPassword: true,
        newPassword: true,
        confirmPassword: true
      } : {})
    };
    setFieldTouched(allFields);
    
    // Validation complète AVANT toute autre action
    if (!validateFormBeforeSubmit()) {
      alert(t('userProfile.errors.formHasErrors'));
      return;
    }

    // Validation et nettoyage STRICTE des emails AVANT ENVOI
    const sanitizedEmails = sanitizeAndValidateEmails();
    if (!sanitizedEmails) {
      return;
    }

    // Vérification supplémentaire pour s'assurer que l'email n'est pas vide
    if (!sanitizedEmails.email || sanitizedEmails.email.trim() === '') {
      alert(t('userProfile.errors.email.empty'));
      return;
    }

    try {
      setIsEditing(true);
      const token = localStorage.getItem('userToken');
      
      let avatarUrl = userData.avatar;
      
      // 1. Uploader la nouvelle image si elle existe
      if (editForm.newAvatar) {
        try {
          setUploadingImage(true);
          const formData = new FormData();
          formData.append('avatar', editForm.newAvatar);
          
          const uploadResponse = await axios.post(
            `${API_URL}/api/user/upload-avatar/${userData.id}`,
            formData,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'multipart/form-data',
              },
            }
          );
          
          if (uploadResponse.status === 200) {
            avatarUrl = uploadResponse.data.avatar;
          }
        } catch (uploadError) {
          console.warn('Échec upload image:', uploadError.message);
          alert(uploadError.message);
        } finally {
          setUploadingImage(false);
        }
      } else if (editForm.avatar === null && userData.avatar) {
        avatarUrl = '';
      }
      
      // 2. Mettre à jour les informations du profil
      const updateData = {
        nom: editForm.nom.trim(),
        email: sanitizedEmails.email,
        telephone: editForm.telephone.trim(),
        adresse: editForm.adresse.trim(),
      };

      // Pour les comptes pro, ajouter les champs spécifiques
      if (isProUser) {
        updateData.nom_pro = editForm.nom_pro.trim();
        updateData.type_pro = editForm.type_pro;
        updateData.descriptions = editForm.descriptions.trim();
        updateData.specialites = editForm.specialites.trim();
        updateData.businessEmail = sanitizedEmails.businessEmail;
        updateData.businessWebsite = editForm.businessWebsite.trim();
        updateData.businessHours = editForm.businessHours.trim();
      }

      if (avatarUrl !== userData.avatar) {
        updateData.avatar = avatarUrl;
      }

      if (editForm.newPassword) {
        updateData.password = editForm.newPassword;
      }

      const response = await axios.put(
        `${API_URL}/api/user/update/${userData.id}`,
        updateData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200) {
        const updatedUserData = {
          ...userData,
          ...updateData,
          avatar: response.data.avatar || avatarUrl || userData.avatar
        };
        
        setUserData(updatedUserData);
        localStorage.setItem('userData', JSON.stringify(updatedUserData));
        
        alert(isProUser 
          ? t('userProfile.profileHeader.updateSuccessPro')
          : t('userProfile.profileHeader.updateSuccess')
        );
        setShowEditModal(false);
        resetForm();
        
        handleProfileUpdate();
      } else {
        throw new Error(t('userProfile.profileHeader.updateError'));
      }
    } catch (error) {
      console.error('Erreur mise à jour profil:', error.response?.data || error.message);
      
      let errorMessage = t('userProfile.profileHeader.updateError');
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message.includes('409') || error.response?.status === 409) {
        errorMessage = t('userProfile.errors.email.alreadyInUse');
      } else if (error.response?.status === 400) {
        errorMessage = t('userProfile.errors.invalidData');
      } else if (error.response?.status === 422) {
        errorMessage = t('userProfile.errors.email.validationFailed');
      }
      
      alert(errorMessage);
    } finally {
      setIsEditing(false);
    }
  };

  // Fonction pour gérer l'upgrade vers compte pro
  const handleUpgradeToPro = () => {
    const emailValidation = validateEmailFormat(editForm.email || userData.email);
    if (!emailValidation.isValid) {
      alert(t('userProfile.errors.email.fixBeforeUpgrade'));
      return;
    }

    if (window.confirm(t('userProfile.proUpgrade.description'))) {
      handleProUpgrade();
    }
  };

  const handleProUpgrade = async () => {
    try {
      setIsEditing(true);
      const token = localStorage.getItem('userToken');
      
      const response = await axios.put(
        `${API_URL}/api/user/upgrade-to-pro/${userData.id}`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200) {
        alert(t('userProfile.proUpgrade.successMessage'));
        fetchUserData();
      }
    } catch (error) {
      console.error('Erreur lors de l\'upgrade:', error);
      alert(t('userProfile.proUpgrade.errorMessage'));
    } finally {
      setIsEditing(false);
    }
  };

  const resetForm = () => {
    setEditForm({
      nom: '',
      email: '',
      telephone: '',
      adresse: '',
      avatar: '',
      newAvatar: null,
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      nom_pro: '',
      type_pro: '',
      descriptions: '',
      specialites: '',
      businessEmail: '',
      businessWebsite: '',
      businessHours: ''
    });
    setFormErrors({});
    setFieldTouched({});
  };

  const handleEditProfile = () => {
    setShowEditModal(true);
  };

  const handleTabPress = (tabId) => {
    setActiveTab(tabId);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <ProfileHeader 
            userData={userData} 
            onUpdate={handleProfileUpdate} 
            userToken={localStorage.getItem('userToken')}
          />
        );
      case 'produits':
        return (
          <div className="w-full">
            <Produit 
              isOwner={true} 
              userId={userData?.id}
            />
          </div>
        );
      case 'verification':
        return <VerificationSection userData={userData} />;
      case 'privacy':
        const privacySettings = {
          language: userData.language || 'fr',
          profileVisibility: userData.profileVisibility || 'public',
          contactPreference: userData.contactPreference || 'both',
          showOnlineStatus: userData.showOnlineStatus !== undefined ? userData.showOnlineStatus : true,
          allowAfterHoursContact: userData.allowAfterHoursContact !== undefined ? userData.allowAfterHoursContact : true,
          emailNotifications: userData.emailNotifications !== undefined ? userData.emailNotifications : true,
          smsNotifications: userData.smsNotifications !== undefined ? userData.smsNotifications : false,
          whatsappNotifications: userData.whatsappNotifications !== undefined ? userData.whatsappNotifications : true,
          allowAnalytics: userData.allowAnalytics !== undefined ? userData.allowAnalytics : true,
          personalizedRecommendations: userData.personalizedRecommendations !== undefined ? userData.personalizedRecommendations : true,
          loginNotifications: userData.loginNotifications !== undefined ? userData.loginNotifications : true,
        };
        
        return <PrivacySettings 
          settings={privacySettings}
          userData={userData}
        />;
      default:
        return (
          <ProfileHeader 
            userData={userData} 
            onUpdate={handleProfileUpdate} 
            userToken={localStorage.getItem('userToken')}
          />
        );
    }
  };

  const renderTabs = () => (
    <div className="bg-white border-b border-gray-200">
      <div className="container mx-auto px-4">
        <div className="flex overflow-x-auto scrollbar-hide space-x-2 py-3">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`flex items-center px-4 py-2 rounded-full transition-colors whitespace-nowrap ${
                activeTab === tab.id 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              onClick={() => handleTabPress(tab.id)}
            >
              <Icon 
                name={tab.icon} 
                size={16} 
                className="mr-2"
                color={activeTab === tab.id ? '#ffffff' : '#64748b'}
              />
              <span className="text-sm font-medium">
                {tab.label}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  // Composant pour afficher une erreur de champ
  const renderFieldError = (fieldName) => {
    if (formErrors[fieldName] && fieldTouched[fieldName]) {
      return (
        <div className="flex items-center mt-1 text-red-600 text-sm">
          <Icon name="AlertCircle" size={14} className="mr-1" />
          <span>{formErrors[fieldName]}</span>
        </div>
      );
    }
    return null;
  };

  // Composant pour afficher un exemple d'email valide
  const renderEmailExample = () => (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
      <div className="text-xs font-semibold text-blue-800 mb-1">
        {t('userProfile.editModal.emailExamples')}
      </div>
      <div className="text-xs text-blue-700 space-y-1">
        <div>• exemple@domaine.com</div>
        <div>• nom.prenom@entreprise.fr</div>
        <div>• contact@societe.co.uk</div>
      </div>
    </div>
  );

  const renderEditModal = () => (
    showEditModal && (
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
        onClick={() => {
          if (!isEditing && !uploadingImage) {
            setShowEditModal(false);
            resetForm();
          }
        }}
      >
        <div 
          className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">
              {isProUser 
                ? t('userProfile.editModal.titlePro') 
                : t('userProfile.editModal.title')
              }
            </h2>
            <button
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              onClick={() => {
                setShowEditModal(false);
                resetForm();
              }}
              disabled={isEditing || uploadingImage}
            >
              <Icon name="X" size={24} color="#64748b" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Section Photo de profil */}
            <div className="mb-8">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                {t('userProfile.editModal.profilePicture')}
              </h3>
              
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 text-center">
                <div className="relative inline-block mb-4">
                  {editForm.avatar ? (
                    <img 
                      src={editForm.avatar} 
                      alt="Avatar"
                      className="w-32 h-32 rounded-full object-cover mx-auto"
                    />
                  ) : (
                    <div className="w-32 h-32 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex flex-col items-center justify-center mx-auto">
                      <Icon name="User" size={48} color="#9ca3af" />
                      <span className="text-xs text-gray-500 mt-2">
                        {t('userProfile.editModal.noPhoto')}
                      </span>
                    </div>
                  )}
                  
                  {uploadingImage && (
                    <div className="absolute inset-0 bg-black bg-opacity-70 rounded-full flex flex-col items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                      <span className="text-white text-xs mt-2">
                        {t('userProfile.editModal.uploading')}
                      </span>
                    </div>
                  )}
                </div>
                
                <p className="text-sm text-gray-600 mb-6">
                  {t('userProfile.editModal.helpText')}
                </p>
                
                <div className="flex flex-wrap justify-center gap-3">
                  <button
                    className="flex items-center px-4 py-2 bg-blue-50 border border-blue-200 text-blue-600 rounded-lg hover:bg-blue-100 disabled:opacity-50 transition-colors"
                    onClick={handlePickImage}
                    disabled={uploadingImage || isEditing}
                  >
                    <Icon name="Image" size={18} className="mr-2" />
                    {t('userProfile.editModal.gallery')}
                  </button>
                  
                  {editForm.avatar && (
                    <button
                      className="flex items-center px-4 py-2 bg-red-50 border border-red-200 text-red-600 rounded-lg hover:bg-red-100 disabled:opacity-50 transition-colors"
                      onClick={handleRemoveAvatar}
                      disabled={uploadingImage || isEditing}
                    >
                      <Icon name="Trash2" size={18} className="mr-2" />
                      {t('userProfile.editModal.remove')}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Informations personnelles */}
            <div className="mb-8">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                {t('userProfile.editModal.personalInfo')}
              </h3>
              
              <div className="space-y-4">
                {/* Nom */}
                <div>
                  <div className="flex items-center mb-1">
                    <label className="text-sm font-medium text-gray-700">
                      {t('userProfile.editModal.fullName')}
                    </label>
                    <span className="text-red-500 ml-1">*</span>
                  </div>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      formErrors.nom && fieldTouched.nom 
                        ? 'border-red-500 bg-red-50' 
                        : 'border-gray-300'
                    }`}
                    value={editForm.nom}
                    onChange={(e) => handleFieldChange('nom', e.target.value)}
                    placeholder={t('userProfile.editModal.fullNamePlaceholder')}
                    disabled={isEditing}
                    onBlur={() => setFieldTouched(prev => ({ ...prev, nom: true }))}
                  />
                  {renderFieldError('nom')}
                </div>

                {/* Email */}
                <div>
                  <div className="flex items-center mb-1">
                    <label className="text-sm font-medium text-gray-700">
                      {t('userProfile.editModal.email')}
                    </label>
                    <span className="text-red-500 ml-1">*</span>
                  </div>
                  <input
                    type="email"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      formErrors.email && fieldTouched.email 
                        ? 'border-red-500 bg-red-50' 
                        : 'border-gray-300'
                    }`}
                    value={editForm.email}
                    onChange={(e) => handleFieldChange('email', e.target.value)}
                    placeholder={t('userProfile.editModal.emailPlaceholder')}
                    disabled={isEditing}
                    onBlur={() => setFieldTouched(prev => ({ ...prev, email: true }))}
                  />
                  {renderFieldError('email')}
                  <p className="text-xs text-gray-500 mt-1 italic">
                    {t('userProfile.editModal.emailHint')}
                  </p>
                  {renderEmailExample()}
                </div>

                {/* Téléphone */}
                <div>
                  <div className="flex items-center mb-1">
                    <label className="text-sm font-medium text-gray-700">
                      {t('userProfile.editModal.phone')}
                    </label>
                    {isProUser && <span className="text-red-500 ml-1">*</span>}
                  </div>
                  <input
                    type="tel"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      formErrors.telephone && fieldTouched.telephone 
                        ? 'border-red-500 bg-red-50' 
                        : 'border-gray-300'
                    }`}
                    value={editForm.telephone}
                    onChange={(e) => handleFieldChange('telephone', e.target.value)}
                    placeholder={t('userProfile.editModal.phonePlaceholder')}
                    disabled={isEditing}
                    onBlur={() => setFieldTouched(prev => ({ ...prev, telephone: true }))}
                  />
                  {renderFieldError('telephone')}
                  <p className="text-xs text-gray-500 mt-1 italic">
                    {isProUser 
                      ? t('userProfile.editModal.phoneHintPro')
                      : t('userProfile.editModal.phoneHint')
                    }
                  </p>
                </div>

                {/* Adresse */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    {t('userProfile.editModal.address')}
                  </label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={editForm.adresse}
                    onChange={(e) => handleFieldChange('adresse', e.target.value)}
                    placeholder={t('userProfile.editModal.addressPlaceholder')}
                    rows="3"
                    disabled={isEditing}
                  />
                  <p className="text-xs text-gray-500 mt-1 italic">
                    {t('userProfile.editModal.addressHint')}
                  </p>
                </div>
              </div>
            </div>

            {/* Section pour les comptes pro */}
            {isProUser && (
              <div className="mb-8">
                <div className="flex items-center mb-4 gap-2">
                  <Icon name="Building" size={20} color="#3b82f6" />
                  <h3 className="text-lg font-bold text-gray-900">
                    {t('userProfile.editModal.proAccount')}
                  </h3>
                  <div className="bg-yellow-500 w-6 h-6 rounded-full flex items-center justify-center">
                    <Icon name="Star" size={12} color="white" />
                  </div>
                </div>
                
                <div className="space-y-4">
                  {/* Nom de l'entreprise */}
                  <div>
                    <div className="flex items-center mb-1">
                      <label className="text-sm font-medium text-gray-700">
                        {t('userProfile.editModal.companyName')}
                      </label>
                      <span className="text-red-500 ml-1">*</span>
                    </div>
                    <input
                      type="text"
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        formErrors.nom_pro && fieldTouched.nom_pro 
                          ? 'border-red-500 bg-red-50' 
                          : 'border-gray-300'
                      }`}
                      value={editForm.nom_pro}
                      onChange={(e) => handleFieldChange('nom_pro', e.target.value)}
                      placeholder={t('userProfile.editModal.companyNamePlaceholder')}
                      disabled={isEditing}
                      onBlur={() => setFieldTouched(prev => ({ ...prev, nom_pro: true }))}
                    />
                    {renderFieldError('nom_pro')}
                    <p className="text-xs text-gray-500 mt-1 italic">
                      {t('userProfile.editModal.companyNameHint')}
                    </p>
                  </div>

                  {/* Email professionnel */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                      {t('userProfile.editModal.businessEmail')}
                    </label>
                    <input
                      type="email"
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        formErrors.businessEmail && fieldTouched.businessEmail 
                          ? 'border-red-500 bg-red-50' 
                          : 'border-gray-300'
                      }`}
                      value={editForm.businessEmail}
                      onChange={(e) => handleFieldChange('businessEmail', e.target.value)}
                      placeholder={t('userProfile.editModal.businessEmailPlaceholder')}
                      disabled={isEditing}
                      onBlur={() => setFieldTouched(prev => ({ ...prev, businessEmail: true }))}
                    />
                    {renderFieldError('businessEmail')}
                    <p className="text-xs text-gray-500 mt-1 italic">
                      {t('userProfile.editModal.businessEmailHint')}
                    </p>
                    {renderEmailExample()}
                  </div>

                  {/* Site web */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                      {t('userProfile.editModal.businessWebsite')}
                    </label>
                    <input
                      type="url"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={editForm.businessWebsite}
                      onChange={(e) => handleFieldChange('businessWebsite', e.target.value)}
                      placeholder={t('userProfile.editModal.businessWebsitePlaceholder')}
                      disabled={isEditing}
                    />
                    <p className="text-xs text-gray-500 mt-1 italic">
                      {t('userProfile.editModal.businessWebsiteHint')}
                    </p>
                  </div>

                  {/* Spécialités */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                      {t('userProfile.editModal.specialties')}
                    </label>
                    <textarea
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={editForm.specialites}
                      onChange={(e) => handleFieldChange('specialites', e.target.value)}
                      placeholder={t('userProfile.editModal.specialtiesPlaceholder')}
                      rows="3"
                      disabled={isEditing}
                    />
                    <p className="text-xs text-gray-500 mt-1 italic">
                      {t('userProfile.editModal.specialtiesHint')}
                    </p>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                      {t('userProfile.editModal.description')}
                    </label>
                    <textarea
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={editForm.descriptions}
                      onChange={(e) => handleFieldChange('descriptions', e.target.value)}
                      placeholder={t('userProfile.editModal.descriptionPlaceholder')}
                      rows="4"
                      disabled={isEditing}
                    />
                    <p className="text-xs text-gray-500 mt-1 italic">
                      {t('userProfile.editModal.descriptionHint')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Changement de mot de passe */}
            <div className="mb-8">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                {t('userProfile.editModal.changePassword')}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {t('userProfile.editModal.passwordNote')}
              </p>
              
              <div className="space-y-4">
                {/* Mot de passe actuel */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    {t('userProfile.editModal.currentPassword')}
                  </label>
                  <input
                    type="password"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      formErrors.currentPassword && fieldTouched.currentPassword 
                        ? 'border-red-500 bg-red-50' 
                        : 'border-gray-300'
                    }`}
                    value={editForm.currentPassword}
                    onChange={(e) => handleFieldChange('currentPassword', e.target.value)}
                    placeholder={t('userProfile.editModal.currentPasswordPlaceholder')}
                    disabled={isEditing}
                    onBlur={() => setFieldTouched(prev => ({ ...prev, currentPassword: true }))}
                  />
                  {renderFieldError('currentPassword')}
                </div>

                {/* Nouveau mot de passe */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    {t('userProfile.editModal.newPassword')}
                  </label>
                  <input
                    type="password"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      formErrors.newPassword && fieldTouched.newPassword 
                        ? 'border-red-500 bg-red-50' 
                        : 'border-gray-300'
                    }`}
                    value={editForm.newPassword}
                    onChange={(e) => handleFieldChange('newPassword', e.target.value)}
                    placeholder={t('userProfile.editModal.newPasswordPlaceholder')}
                    disabled={isEditing}
                    onBlur={() => setFieldTouched(prev => ({ ...prev, newPassword: true }))}
                  />
                  {renderFieldError('newPassword')}
                  <p className="text-xs text-gray-500 mt-1 italic">
                    {t('userProfile.editModal.newPasswordHint')}
                  </p>
                </div>

                {/* Confirmer mot de passe */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    {t('userProfile.editModal.confirmPassword')}
                  </label>
                  <input
                    type="password"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      formErrors.confirmPassword && fieldTouched.confirmPassword 
                        ? 'border-red-500 bg-red-50' 
                        : 'border-gray-300'
                    }`}
                    value={editForm.confirmPassword}
                    onChange={(e) => handleFieldChange('confirmPassword', e.target.value)}
                    placeholder={t('userProfile.editModal.confirmPasswordPlaceholder')}
                    disabled={isEditing}
                    onBlur={() => setFieldTouched(prev => ({ ...prev, confirmPassword: true }))}
                  />
                  {renderFieldError('confirmPassword')}
                </div>
              </div>
            </div>

            {/* Informations */}
            <div className="space-y-3">
              <div className="flex items-start bg-gray-50 p-3 rounded-lg">
                <Icon name="Info" size={16} color="#6b7280" className="mt-0.5 mr-2 flex-shrink-0" />
                <p className="text-sm text-gray-600">
                  {t('userProfile.editModal.requiredFieldsInfo')}
                </p>
              </div>
              
              <div className="flex items-start bg-green-50 p-3 rounded-lg border border-green-200">
                <Icon name="ShieldCheck" size={16} color="#10b981" className="mt-0.5 mr-2 flex-shrink-0" />
                <p className="text-sm text-green-700 font-medium">
                  {t('userProfile.editModal.emailValidationInfo')}
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-3 p-6 border-t border-gray-200">
            <button
              className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 disabled:opacity-50 transition-colors"
              onClick={() => {
                setShowEditModal(false);
                resetForm();
              }}
              disabled={isEditing || uploadingImage}
            >
              {t('userProfile.editModal.cancel')}
            </button>
            
            <button
              className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center justify-center"
              onClick={handleUpdateProfile}
              disabled={isEditing || uploadingImage}
            >
              {(isEditing || uploadingImage) ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  <Icon name="Check" size={20} color="white" className="mr-2" />
                  {isProUser 
                    ? t('userProfile.editModal.savePro')
                    : t('userProfile.editModal.save')
                  }
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    )
  );

  if (isLoading || !userData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex flex-col items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">{t('userProfile.loading.loadingProfile')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Modal d'édition */}
      {renderEditModal()}
      
      {/* En-tête du Profil */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            {/* Avatar */}
            <div className="relative">
              <div className="w-24 h-24 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center">
                {userData.avatar ? (
                  <img 
                    src={userData.avatar} 
                    alt={userData.nom}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : null}
                {!userData.avatar && (
                  <div className="w-full h-full flex items-center justify-center">
                    <Icon name="User" size={48} color="#64748b" />
                  </div>
                )}
              </div>
              {userData.isPro && (
                <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1 shadow">
                  <Icon name="CheckCircle" size={20} color="#10b981" />
                </div>
              )}
            </div>

            {/* Informations */}
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <h1 className="text-2xl font-bold text-gray-900">{userData.nom}</h1>
                {userData.isPro && (
                  <div className="bg-yellow-500 text-white text-xs font-semibold px-3 py-1.5 rounded-full flex items-center">
                    <Icon name="Star" size={12} className="mr-1.5" />
                    PRO
                  </div>
                )}
              </div>
              
              <div className="space-y-1 mb-6">
                <div className="flex items-center text-gray-600">
                  <Icon name="MapPin" size={14} className="mr-2" />
                  <span>{userData.adresse}</span>
                </div>
                <div className="flex items-center text-gray-500 text-sm">
                  <Icon name="Mail" size={14} className="mr-2" />
                  <span>{userData.email}</span>
                </div>
                <div className="flex items-center text-gray-500 text-sm">
                  <Icon name="Phone" size={14} className="mr-2" />
                  <span>{userData.telephone || t('userProfile.emptyFields.phone')}</span>
                </div>
              </div>

              {/* Statistiques */}
              <div className="flex flex-wrap gap-6 mb-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{statsData.activeListings}</div>
                  <div className="text-sm text-gray-600">{t('userProfile.profileHeader.stats.listings')}</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{statsData.totalSales}</div>
                  <div className="text-sm text-gray-600">{t('userProfile.profileHeader.stats.sales')}</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">{statsData.responseRate}%</div>
                  <div className="text-sm text-gray-600">{t('userProfile.profileHeader.stats.response')}</div>
                </div>
              </div>

              {/* Boutons d'action */}
              <div className="flex flex-wrap gap-3">
                {!userData.isPro && (
                  <button 
                    className="inline-flex items-center px-4 py-2 bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-lg hover:bg-yellow-100 transition-colors"
                    onClick={handleUpgradeToPro}
                  >
                    <Icon name="Sparkles" size={16} className="mr-2" />
                    {t('userProfile.proUpgrade.becomePro')}
                  </button>
                )}
                
                <button 
                  className="inline-flex items-center px-4 py-2 bg-blue-50 border border-blue-200 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                  onClick={handleEditProfile}
                >
                  <Icon name="Edit" size={16} className="mr-2" />
                  {t('userProfile.editModal.editProfile')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation par Onglets */}
      {renderTabs()}

      {/* Indicateur de rafraîchissement */}
      {refreshing && (
        <div className="flex items-center justify-center py-3 bg-blue-50">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
          <span className="text-sm text-blue-600">{t('userProfile.loading.refreshing')}</span>
        </div>
      )}

      {/* Contenu de l'onglet */}
      <div className="container mx-auto px-4 py-6">
        {renderTabContent()}
      </div>

      {/* Styles CSS inline */}
      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default UserProfile;